import { Code2, Bot, Megaphone, Palette, ServerCog, Boxes, Building2, Home, Church, GraduationCap,
  Workflow, Target, PencilRuler, Rocket, Search, PhoneCall, CalendarCheck, BarChart3, Mail,
  MessageSquare, Globe, QrCode, GitBranch, Zap, Hammer, Wrench, Briefcase, Store, TrendingUp,
  ClipboardCheck, Users, LayoutGrid, ShieldCheck } from "lucide-react";

export const SERVICES = [
  { slug: "web-development", name: "Web Design & Development", icon: Code2,
    tagline: "High-performance websites engineered to convert.",
    desc: "Fast, responsive, SEO-ready websites and web apps built on modern frameworks — designed around your customer's journey.",
    points: ["Responsive marketing sites & web apps", "SEO & Core Web Vitals performance", "CMS & headless architectures", "Conversion-focused UX"] },
  { slug: "ai-automation", name: "AI & Automation", icon: Bot,
    tagline: "Put repetitive work on autopilot.",
    desc: "We identify high-leverage processes and wire them to AI and automation so your team spends time on people, not paperwork.",
    points: ["Workflow automation", "AI summarization & assistants", "Data enrichment & routing", "Human-in-the-loop safeguards"] },
  { slug: "digital-marketing", name: "Digital Marketing", icon: Megaphone,
    tagline: "Reach the right people and measure what works.",
    desc: "Attribution-first marketing. Every campaign is tracked from first touch to outcome so budget follows results.",
    points: ["Campaign strategy & attribution", "Landing pages & lead capture", "Email & social", "Analytics & reporting"] },
  { slug: "branding", name: "Branding & Creative", icon: Palette,
    tagline: "A brand system that scales with you.",
    desc: "Identity, messaging and creative systems that stay consistent across every product, channel and touchpoint.",
    points: ["Logo & identity systems", "Messaging & positioning", "Design systems", "Print & digital assets"] },
  { slug: "it-solutions", name: "IT Solutions", icon: ServerCog,
    tagline: "Reliable infrastructure, quietly handled.",
    desc: "Practical IT: secure setups, integrations and systems that keep your organization running without the headaches.",
    points: ["Systems & integrations", "Security & access", "Cloud & hosting", "Support & maintenance"] },
  { slug: "systems-integration", name: "Systems Integration", icon: Boxes,
    tagline: "Connect the tools you already use.",
    desc: "We connect your website, CRM, marketing and back-office tools into one coherent system with reliable data flow.",
    points: ["API & webhook integrations", "Data sync & migration", "Attribution pipelines", "Field-ownership rules"] },
];

export const SOLUTIONS = [
  { slug: "small-business", name: "Small Business", icon: Building2,
    tagline: "Everything you need to capture and keep customers.",
    desc: "Websites, lead capture, follow-up and automation bundled into a system a small team can actually run.",
    outcomes: ["Never miss a lead", "Automated follow-up", "One source of truth", "Clear reporting"] },
  { slug: "real-estate", name: "Real Estate", icon: Home,
    tagline: "From first inquiry to closed deal.",
    desc: "Lead attribution, pipeline stages and follow-up workflows tuned for agents and brokerages.",
    outcomes: ["Lead source attribution", "Pipeline from lead to close", "Fast mobile follow-up", "Team accountability"] },
  { slug: "churches-nonprofits", name: "Churches & Nonprofits", icon: Church,
    tagline: "Make sure nobody falls through the cracks.",
    desc: "Capture visitors and supporters from forms, QR codes, events and outreach — and ensure someone follows up.",
    outcomes: ["Visitor & donor capture", "Outreach & event tracking", "Follow-up accountability", "Weekly brief reporting"] },
  { slug: "education", name: "Education Programs", icon: GraduationCap,
    tagline: "Connect with students and families.",
    desc: "Enrollment inquiries, event registration and ongoing communication in one organized system.",
    outcomes: ["Inquiry & enrollment capture", "Event registration", "Family communication", "Program reporting"] },
];

export const WHAT_WE_DO = [
  { icon: Boxes, title: "Software Products", desc: "Scalable SaaS products like AllToMAIDIt CRM — built once, grown continuously." },
  { icon: Bot, title: "AI & Automation", desc: "Automate the repetitive so your team focuses on relationships and growth." },
  { icon: Code2, title: "Web & Development", desc: "High-performance sites and custom technology tailored to your problem." },
  { icon: Target, title: "Strategy & Systems", desc: "We design the system around your customer — not the other way around." },
];

export const HOW_WE_WORK = [
  { icon: Target, step: "01", title: "Audit", desc: "We map your customer journey and find where contacts and revenue leak." },
  { icon: PencilRuler, step: "02", title: "Design", desc: "We design the system, workflows and brand around your actual problem." },
  { icon: Workflow, step: "03", title: "Build", desc: "We build it — products, automation and integrations that work together." },
  { icon: Rocket, step: "04", title: "Scale", desc: "We measure outcomes and expand what works across your organization." },
];

export const INDUSTRIES = ["Small Business", "Real Estate", "Churches & Nonprofits", "Education", "Contractors", "Sports Organizations", "Sales Teams", "Community Organizations"];

/* ===== Lead-to-Client System ===== */

export const SYSTEM_PILLARS = [
  { key: "attract", title: "ATTRACT", icon: Globe, items: ["Website", "Landing Pages", "SEO", "Advertising"] },
  { key: "capture", title: "CAPTURE", icon: QrCode, items: ["Smart Forms", "QR Codes", "Lead Capture"] },
  { key: "organize", title: "ORGANIZE", icon: Users, items: ["AllToMAIDIt CRM", "Contact Management", "Source Tracking", "Pipeline"] },
  { key: "followup", title: "FOLLOW UP", icon: PhoneCall, items: ["Automated Workflows", "Tasks", "Email / SMS", "Appointment Scheduling"] },
  { key: "convert", title: "CONVERT", icon: Target, items: ["Sales Pipeline", "Next Actions", "Accountability"] },
  { key: "measure", title: "MEASURE", icon: BarChart3, items: ["Lead Sources", "Response Time", "Conversions", "Campaign Performance"] },
];

export const PROCESS = [
  { step: "01", title: "AUDIT", icon: Search, desc: "We identify where leads currently enter your business and where opportunities are being lost." },
  { step: "02", title: "BUILD", icon: Hammer, desc: "We connect or build the website, forms, CRM, tracking and automation your business needs." },
  { step: "03", title: "LAUNCH", icon: Rocket, desc: "Your new Lead-to-Client System begins capturing and organizing opportunities." },
  { step: "04", title: "OPTIMIZE", icon: TrendingUp, desc: "We measure performance and improve the system over time." },
];

export const MODULES = [
  { name: "Website", icon: Globe }, { name: "Landing Pages", icon: LayoutGrid }, { name: "CRM", icon: Users },
  { name: "Forms", icon: ClipboardCheck }, { name: "QR Codes", icon: QrCode }, { name: "AI Automation", icon: Bot },
  { name: "Email Follow-Up", icon: Mail }, { name: "SMS Follow-Up", icon: MessageSquare }, { name: "Appointment Booking", icon: CalendarCheck },
  { name: "Pipeline", icon: GitBranch }, { name: "Analytics", icon: BarChart3 }, { name: "Source Tracking", icon: Target },
  { name: "Advertising Integration", icon: Megaphone }, { name: "Reporting", icon: ShieldCheck },
];

export const IMPACTS = [
  { title: "Respond Faster", icon: Zap }, { title: "Know Where Leads Come From", icon: Target },
  { title: "Stop Forgetting Follow-Ups", icon: PhoneCall }, { title: "Keep Your Team Accountable", icon: Users },
  { title: "Understand What Marketing Works", icon: BarChart3 }, { title: "Turn More Opportunities Into Customers", icon: TrendingUp },
];

export const INDUSTRY_CARDS = [
  { slug: "home-services", name: "Home Services", icon: Wrench, desc: "HVAC, plumbing, electrical, landscaping and more — capture every call and form.", go: "home-services" },
  { slug: "contractors", name: "Contractors", icon: Hammer, desc: "Remodeling, roofing and restoration crews that can't afford to miss a high-value lead.", go: "contractors" },
  { slug: "real-estate", name: "Real Estate", icon: Home, desc: "Agents and brokerages tracking inquiries from first touch to closing.", go: "real-estate" },
  { slug: "professional-services", name: "Professional Services", icon: Briefcase, desc: "Consultants and firms who live and die by fast, organized follow-up.", go: null },
  { slug: "small-business", name: "Small Business", icon: Store, desc: "Lead-driven local businesses that want one system instead of five tools.", go: null },
];

/* Reusable paid-ad campaign landing pages: /go/:slug */
export const GO_CAMPAIGNS = {
  "contractors": {
    campaign: "Contractors Campaign", industry: "Contractors", icon: Hammer,
    headline: "EVERY MISSED LEAD IS A JOB YOUR COMPETITOR JUST WON.",
    sub: "AllToMAIDIt captures every call, form and QR scan, tracks where it came from, and makes sure someone follows up — before the lead goes cold.",
    pains: ["Leads buried in texts, voicemails and sticky notes", "No idea which ads actually book jobs", "Estimates that never got a follow-up call"],
  },
  "home-services": {
    campaign: "Home Services Campaign", industry: "Home Services", icon: Wrench,
    headline: "STOP LETTING BOOKED JOBS SLIP THROUGH THE CRACKS.",
    sub: "Capture every service request, respond in minutes, and keep your techs and office on the same page.",
    pains: ["Calls missed during jobs", "Forms that go to an inbox nobody checks", "No follow-up on quotes"],
  },
  "real-estate": {
    campaign: "Real Estate Campaign", industry: "Real Estate", icon: Home,
    headline: "THE FASTEST AGENT TO FOLLOW UP USUALLY WINS THE LISTING.",
    sub: "Capture every inquiry from your site, ads and referrals, and never let a lead sit without a next action.",
    pains: ["Leads scattered across portals and email", "Slow first response", "No attribution on which source closes"],
  },
  "restoration": {
    campaign: "Restoration Campaign", industry: "Restoration", icon: ShieldCheck,
    headline: "WHEN DISASTER STRIKES, THE FIRST CALLBACK WINS THE JOB.",
    sub: "Capture emergency leads instantly and route them to someone who will respond — day or night.",
    pains: ["Emergency leads lost after hours", "No tracking on ad spend", "Follow-up falls through during busy periods"],
  },
};

export const BUSINESS_TYPES = ["Contractor", "Restoration", "Roofing", "HVAC", "Plumbing", "Electrical", "Remodeling", "Landscaping", "Home Services", "Real Estate", "Professional Services", "Other"];

export const AUDIT_OPTIONS = {
  lead_sources: ["Google", "Google Ads", "Facebook / Instagram", "Website", "Referrals", "Phone", "Events", "Other"],
  leads_per_month: ["0–10", "11–25", "26–50", "51–100", "100+"],
  tracking_method: ["Spreadsheet", "CRM", "Email", "Phone", "Multiple Systems", "Not Consistently Tracked", "Other"],
  follow_up_speed: ["Immediately", "Within 1 Hour", "Same Day", "Next Day", "Longer", "Not Sure"],
  customer_value: ["Under $500", "$500–$2,500", "$2,500–$10,000", "$10,000–$25,000", "$25,000+"],
  biggest_challenge: ["Generating Leads", "Following Up", "Tracking Leads", "Converting Leads", "Marketing Attribution", "Automation", "Website Performance", "Other"],
  preferred_contact: ["Phone", "Email", "Text"],
};

export const LTC_FAQ = [
  { q: "Do I have to replace my current website?", a: "No. We can connect your existing website to the system, or build new high-converting pages — whatever your situation needs." },
  { q: "Is this just a CRM?", a: "The CRM is the engine, but the Lead-to-Client System also includes capture, follow-up, attribution and reporting assembled around your business." },
  { q: "What does it cost?", a: "Implementation varies by what you need, so we start with a free audit rather than a one-size-fits-all price. The CRM is also available separately in Start, Grow and Scale tiers." },
  { q: "How fast can we launch?", a: "Simple setups can launch quickly; larger builds depend on scope. The audit gives you a clear plan and timeline." },
  { q: "Do you lock me into long contracts?", a: "We focus on results. The audit and proposal spell out exactly what's included before you commit to anything." },
];
